import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import { waitForCompanyID } from '@/js/external-js/variableModules/utils/waitForCompanyID.util';

const ROLES_API = '/v1/roles';

// Interface for the processed User Role
export interface UserRole {
  id: string;
  userId: string;
  teamName: string;
  ismsRole: string;
  ismsCommittee: string;
  dateAdded: Date;
  dateUpdated: Date;
  addedBy?: string;
  updatedBy?: string;
}

// Interface for the processed ISMS Role
export interface ISMSRole {
  id: string;
  roleName: string;
  roleDescription: string;
  dateAdded: Date;
  dateUpdated: Date;
}

export interface CurrentUsers {
  businessUnit: string;
  dateAdded: {
    __time__: string;
  };
  ismsCommittee: string;
  ismsRole: string;
  lastUpdated: any;
  lastUpdatedBy: string;
  licensesUsed: string;
  role: string;
  teamName: string;
  userID: string;
  userNumber: string;
}

/**
 * Fetches all user roles via Thalassa API
 */
export async function getAllUserRoles(): Promise<UserRole[]> {
  try {
    const companyID = await waitForCompanyID();
    
    const res = await deiterateAPI.GET(`${ROLES_API}/users`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
    });

    if (!res.roles || res.roles.length === 0) {
      console.warn('No users data found');
      return [];
    }

    // Convert ISO date strings to Date objects
    return res.roles.map((role: any) => ({
      ...role,
      dateAdded: role.dateAdded ? new Date(role.dateAdded) : new Date(),
      dateUpdated: role.dateUpdated ? new Date(role.dateUpdated) : new Date(),
    }));
  } catch (error) {
    console.error('Error fetching user roles:', error);
    throw new Error('Failed to load user roles');
  }
}

/**
 * Fetches all ISMS roles via Thalassa API
 */
export async function getISMSRoles(): Promise<{
  ismsRoles: ISMSRole[];
  ismsCommitteeRoles: ISMSRole[];
}> {
  try {
    const companyID = await waitForCompanyID();
    
    const res = await deiterateAPI.GET(`${ROLES_API}/isms`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID }
    });

    const processRoles = (roles: any[]): ISMSRole[] => {
      if (!roles) return [];
      return roles.map((role: any) => ({
        ...role,
        dateAdded: role.dateAdded ? new Date(role.dateAdded) : new Date(),
        dateUpdated: role.dateUpdated ? new Date(role.dateUpdated) : new Date(),
      }));
    };

    return {
      ismsRoles: processRoles(res.ismsRoles),
      ismsCommitteeRoles: processRoles(res.ismsCommitteeRoles),
    };
  } catch (error) {
    console.error('Error fetching ISMS roles:', error);
    throw new Error('Failed to load ISMS roles');
  }
}

/**
 * Updates a user's role via Thalassa API
 */
export async function updateUserRole(
  userId: string,
  updates: Partial<Omit<UserRole, 'id' | 'userId' | 'dateAdded' | 'addedBy'>>
): Promise<void> {
  try {
    const companyID = await waitForCompanyID();
    
    await deiterateAPI.PUT(`${ROLES_API}/users/${userId}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': companyID, 'Company-ID': companyID },
      body: JSON.stringify(updates)
    });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw new Error('Failed to update user role');
  }
}

/**
 * Sets up a polling listener for user roles changes
 */
export function onUserRolesChange(
  callback: (roles: UserRole[]) => void
): () => void {
  let isSubscribed = true;
  let lastRoles: UserRole[] = [];

  const checkForUpdates = async () => {
    if (!isSubscribed) return;
    
    try {
      const roles = await getAllUserRoles();
      
      if (JSON.stringify(roles) !== JSON.stringify(lastRoles)) {
        lastRoles = roles;
        callback(roles);
      }
    } catch (error) {
      console.error('Error in roles listener:', error);
    }
    
    if (isSubscribed) {
      setTimeout(checkForUpdates, 10000);
    }
  };

  checkForUpdates();

  return () => {
    isSubscribed = false;
  };
}

// Export all functions
export default {
  getAllUserRoles,
  getISMSRoles,
  updateUserRole,
  onUserRolesChange,
};
